import { convexQuery } from '@convex-dev/react-query'
import { msg } from '@lingui/core/macro'
import { Trans, useLingui } from '@lingui/react/macro'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, Navigate } from '@tanstack/react-router'
import { IconSofaOutline18 } from 'nucleo-ui-outline-18'
import { MoodboardsView } from '~/components/moodboards/moodboards-view'
import { RoomPage } from '~/components/products/room-page'
import { RoomsTab } from '~/components/products/rooms-tab'
import { useProjectDetail } from '~/components/projects/project-detail'
import { NO_ROOM } from '~/components/projects/project-detail-search'
import { Button } from '~/components/ui/button'
import { EmptyState } from '~/components/ui/empty-state'
import { Spinner } from '~/components/ui/spinner'
import { titleFor } from '~/lib/route-title'
import { api } from '~convex/_generated/api'

/**
 * AMBIENTES — the rooms of a project.
 *
 * Two states share the destination, told apart by the search rather than by
 * a route, so going back is a search change and never a navigation: `?room`
 * opens one room, and no room is the grid. `?board` opens a moodboard from
 * inside a room without leaving it.
 */
const ProjectRoomsRoute = () => {
  const { t } = useLingui()
  const project = useProjectDetail()
  const { board, room } = Route.useSearch()
  const navigate = Route.useNavigate()
  const roomsQuery = useQuery(convexQuery(api.rooms.listForProject, { projectId: project._id }))
  const roomsData = roomsQuery.data

  if (project.wonAt === undefined) {
    return <Navigate from={Route.fullPath} replace to=".." />
  }
  if (room !== undefined && room !== NO_ROOM && roomsData === undefined) {
    if (roomsQuery.isError) {
      return (
        <div className="flex justify-center py-16">
          <EmptyState
            description={t`Check your connection and try again.`}
            icon={IconSofaOutline18}
            title={t`Couldn't load rooms`}
          >
            <Button
              disabled={roomsQuery.isFetching}
              onClick={() => void roomsQuery.refetch()}
              size="sm"
              variant="outline"
            >
              <Trans>Try again</Trans>
            </Button>
          </EmptyState>
        </div>
      )
    }
    return (
      <div className="flex justify-center py-16">
        <Spinner className="size-5" />
      </div>
    )
  }
  const resolvedRoomId = roomsData?.rooms.find((candidate) => candidate._id === room)?._id
  const roomId = room === NO_ROOM ? null : resolvedRoomId
  // A board here is subordinate to a room. There is no board-only state on
  // the rooms route: requiring the resolved scope prevents an omitted or
  // stale room parameter from disabling MoodboardsView's room guard.
  if (board !== undefined && roomId !== undefined) {
    return (
      <MoodboardsView
        moodboardId={board}
        onOpen={(next) => void navigate({ replace: true, search: { board: next, room } })}
        projectId={project._id}
        readOnly={project.outcome === 'lost'}
        roomId={roomId}
        workspaceId={project.workspaceId}
      />
    )
  }
  if (roomId !== undefined) {
    return (
      <div className="px-5 pb-6">
        <RoomPage
          onBack={() => void navigate({ replace: true, search: { room: undefined } })}
          projectId={project._id}
          roomId={roomId}
        />
      </div>
    )
  }
  return (
    <div className="px-5 pb-6">
      <RoomsTab
        onOpenRoom={(roomId) =>
          void navigate({ replace: true, search: { room: roomId ?? NO_ROOM } })
        }
        projectId={project._id}
      />
    </div>
  )
}

export const Route = createFileRoute('/_authenticated/$workspaceSlug/projects/$projectId/rooms')({
  component: ProjectRoomsRoute,
  head: () => ({ meta: [{ title: titleFor(msg`Rooms`) }] }),
  validateSearch: (search: Record<string, unknown>): { board?: string; room?: string } => ({
    // Keep opaque URL text raw. Moodboards and rooms are resolved against
    // project-scoped query results before a typed id reaches a component.
    board: typeof search.board === 'string' ? search.board : undefined,
    room: typeof search.room === 'string' ? search.room : undefined,
  }),
})
